/**
 * How the Command Sequences drawer words and times a run: the label and tag
 * colour of every run and step status, the time a step has taken against its
 * timeout, and the command a run was started for.
 *
 * Pure, so the drawer's arithmetic is testable without rendering it. The status
 * tables are `Record`s over the generated enums, so a status the schema adds
 * fails to compile here instead of rendering as a blank tag.
 */
import type { ControlMessageT } from "../../../flatbuffers/generated/control/control-message";
import { DeviceControlMessage } from "../../../flatbuffers/generated/control/device-control-message";
import type { ElevatorControlMessageT } from "../../../flatbuffers/generated/control/elevator-control-message";
import type { NormalControlT } from "../../../flatbuffers/generated/control/normal-control";
import type { ValuedControlT } from "../../../flatbuffers/generated/control/valued-control";
import type { BitMaskMessageT } from "../../../flatbuffers/generated/ln2/bit-mask-message";
import { EncodedMessageContent } from "../../../flatbuffers/generated/ln2/encoded-message-content";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import type { OneHotMessageT } from "../../../flatbuffers/generated/ln2/one-hot-message";
import type { FrontEndMessageT } from "../../../flatbuffers/generated/front-end/front-end-message";
import { SequenceRunStatus } from "../../../flatbuffers/generated/front-end/sequence-run-status";
import type { SequenceStepProgressT } from "../../../flatbuffers/generated/front-end/sequence-step-progress";
import { SequenceStepStatus } from "../../../flatbuffers/generated/front-end/sequence-step-status";
import type { WithheldMark } from "../../../interfaces/Monitoring.interface";
import type { SequenceRunView } from "./sequenceRuns";

/** How a status reads: its word, an Ant Design tag colour, and what it means. */
export interface StatusDisplay {
  label: string;
  color: string;
  meaning: string;
}

export const RUN_STATUS_DISPLAY: Record<SequenceRunStatus, StatusDisplay> = {
  [SequenceRunStatus.Running]: {
    label: "Running",
    color: "processing",
    meaning: "Steps remain.",
  },
  [SequenceRunStatus.Completed]: {
    label: "Completed",
    color: "success",
    meaning: "Every step passed.",
  },
  [SequenceRunStatus.Failed]: {
    label: "Failed",
    color: "error",
    meaning: "A step after the release failed or timed out; the always-run steps still ran.",
  },
  [SequenceRunStatus.Withheld]: {
    label: "Withheld",
    color: "warning",
    meaning: "A step before the release failed, so the command was not sent.",
  },
  [SequenceRunStatus.Skipped]: {
    label: "Skipped",
    color: "default",
    meaning: "The preconditions did not hold, so the command went through without the sequence.",
  },
  [SequenceRunStatus.Released]: {
    label: "Released",
    color: "orange",
    meaning: "A step before the release failed, and the command was sent anyway.",
  },
};

export const STEP_STATUS_DISPLAY: Record<SequenceStepStatus, StatusDisplay> = {
  [SequenceStepStatus.Pending]: {
    label: "Pending",
    color: "default",
    meaning: "Not reached yet.",
  },
  [SequenceStepStatus.Active]: {
    label: "Active",
    color: "processing",
    meaning: "Running now.",
  },
  [SequenceStepStatus.Passed]: {
    label: "Passed",
    color: "success",
    meaning: "Observed, sent, released or acknowledged.",
  },
  [SequenceStepStatus.TimedOut]: {
    label: "Timed out",
    color: "error",
    meaning: "Its timeout, or the run's budget, ran out first.",
  },
  [SequenceStepStatus.Failed]: {
    label: "Failed",
    color: "error",
    meaning: "It could not be done.",
  },
  [SequenceStepStatus.Skipped]: {
    label: "Skipped",
    color: "default",
    meaning: "Not run: an earlier step failed, it does not apply, or another run will run it.",
  },
};

/** A status the schema has added since this build: shown by number, never blank. */
function unknownStatus(value: number): StatusDisplay {
  return {
    label: `Status ${value}`,
    color: "default",
    meaning: "A status this page does not know.",
  };
}

export function runStatusDisplay(status: SequenceRunStatus): StatusDisplay {
  return RUN_STATUS_DISPLAY[status] ?? unknownStatus(status);
}

export function stepStatusDisplay(status: SequenceStepStatus): StatusDisplay {
  return STEP_STATUS_DISPLAY[status] ?? unknownStatus(status);
}

/**
 * How long a run has taken by `nowMs`. A report states the elapsed time when
 * VTS sent it, so a run still Running counts on from when its report arrived;
 * a finished run's time is final.
 */
export function runElapsedMs(
  run: Pick<SequenceRunView, "status" | "receivedAtMs" | "progress">,
  nowMs: number,
): number {
  const reported = run.progress.elapsedMs;
  if (run.status !== SequenceRunStatus.Running) return reported;
  return reported + Math.max(0, nowMs - run.receivedAtMs);
}

/** How long a step has taken by `nowMs`: only the active step of a running run counts on. */
export function stepElapsedMs(
  step: Pick<SequenceStepProgressT, "status" | "elapsedMs">,
  run: Pick<SequenceRunView, "status" | "receivedAtMs">,
  nowMs: number,
): number {
  if (run.status !== SequenceRunStatus.Running || step.status !== SequenceStepStatus.Active) {
    return step.elapsedMs;
  }
  return step.elapsedMs + Math.max(0, nowMs - run.receivedAtMs);
}

/** "850 ms", "3.0 s", "2 min 05 s". */
export function formatDuration(ms: number): string {
  const whole = Math.max(0, Math.round(ms));
  if (whole < 1_000) return `${whole} ms`;
  if (whole < 60_000) return `${(whole / 1_000).toFixed(1)} s`;
  const seconds = Math.floor(whole / 1_000);
  return `${Math.floor(seconds / 60)} min ${String(seconds % 60).padStart(2, "0")} s`;
}

/** "1.2 s of 3.0 s"; a step without a timeout (a Release) shows its time alone. */
export function stepTiming(elapsedMs: number, timeoutMs: number): string {
  if (timeoutMs <= 0) return formatDuration(elapsedMs);
  return `${formatDuration(elapsedMs)} of ${formatDuration(timeoutMs)}`;
}

/** The share of its timeout a step has used, 0–100, or null for a step without one. */
export function stepPercent(elapsedMs: number, timeoutMs: number): number | null {
  if (timeoutMs <= 0) return null;
  return Math.min(100, Math.round((Math.max(0, elapsedMs) / timeoutMs) * 100));
}

/** A step's label; VTS always sends one, but a blank one still reads. */
export function stepLabel(step: Pick<SequenceStepProgressT, "label">, index: number): string {
  const text = typeof step.label === "string" ? step.label.trim() : "";
  return text === "" ? `Step ${index + 1}` : text;
}

/**
 * The Elevator Control command a run was started for, as the floor and leaf a
 * withheld mark names, so `withheldNoticeSubject` can name its car, floor and
 * command; null for any other message.
 */
export function commandedMark(fem: FrontEndMessageT, atMs: number): WithheldMark | null {
  const encoded = fem.msg?.msg;
  if (!encoded) return null;
  let inner: BitMaskMessageT | OneHotMessageT | null = null;
  if (encoded.contentType === EncodedMessageContent.BitMaskMessage) {
    inner = encoded.content as BitMaskMessageT;
  } else if (encoded.contentType === EncodedMessageContent.OneHotMessage) {
    inner = encoded.content as OneHotMessageT;
  }
  if (inner?.messageType !== MessageContent.Control_ControlMessage) return null;
  const control = inner.message as ControlMessageT | null;
  if (control?.controlType !== DeviceControlMessage.ElevatorControlMessage) return null;
  const elevator = control.control as ElevatorControlMessageT | null;
  const leaf = (elevator?.control as NormalControlT | ValuedControlT | null)?.message;
  if (elevator === null || leaf === undefined) return null;
  return { position: elevator.position, control: leaf, atMs };
}
